import {
  CheckCircleIcon,
  MapPinIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import React from 'react';

import type { AddressMatchResult } from '../../../../utils/addressMatching';
import { generateEntryKey } from '../../../../utils/nameMatching';
import Badge from '../../../common/Badge';
import type { MatchedPair } from '../ImportDetail.module';

type AddressEntry =
  | (AddressMatchResult & { mode: 'suggestion'; status: 'pending'; pairKey: string })
  | ({
    mode: 'existing';
    status: MatchedPair['status'];
    pairKey: string;
    fileEntry: MatchedPair['fileEntry'];
    rnicEntry: MatchedPair['rnicEntry'];
    confidenceScore?: number;
  });

interface AddressMatchStepProps {
  entries: AddressEntry[];
  fileAddressMap: Map<string, string>;
  rnicAddressMap: Map<string, string>;
  onAccept: (suggestion: AddressMatchResult) => void;
  onReject: (pairKey: string) => void;
  onChangeStatus: (
    fileKey: string,
    rnicKey: string,
    status: 'validated' | 'rejected' | 'pending',
  ) => void;
}

const AddressMatchStep: React.FC<AddressMatchStepProps> = ({
  entries,
  fileAddressMap,
  rnicAddressMap,
  onAccept,
  onReject,
  onChangeStatus,
}) => {
  const hasEntries = entries.length > 0;

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-indigo-800">
          <MapPinIcon className="h-5 w-5" />
          Correspondance suggéré par adresse ({entries.length})
        </h3>
      </div>
      <p className="mb-4 text-sm text-indigo-700">
        Suggestions basées sur la similarité des adresses pour les entrées restantes. Validez pour créer la correspondance ou rejetez pour conserver le traitement manuel.
      </p>

      {!hasEntries ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
          Aucune correspondance proposée par adresse pour le moment.
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => {
            const fileKey = generateEntryKey(entry.fileEntry.immatriculation, entry.fileEntry.name);
            const rnicKey = generateEntryKey(entry.rnicEntry.immatriculation, entry.rnicEntry.name);
            let fileAddress = fileAddressMap.get(fileKey) || '';
            let rnicAddress = rnicAddressMap.get(rnicKey) || '';

            if (entry.mode === 'suggestion') {
              if (!fileAddress) {fileAddress = entry.fileEntry.address;}
              if (!rnicAddress) {rnicAddress = entry.rnicEntry.address;}
            }

            return (
              <div key={entry.pairKey} className="rounded-lg border border-indigo-100 bg-white p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded border border-red-100 bg-red-50 p-3">
                    <p className="mb-1 text-xs font-medium text-red-600">Fichier importé (pas dans RNIC)</p>
                    <p className="font-semibold text-gray-800">{entry.fileEntry.name}</p>
                    <p className="font-mono text-sm text-gray-500">{entry.fileEntry.immatriculation || '-'}</p>
                    <p className="mt-2 text-xs text-gray-600">
                      <span className="font-semibold">Adresse :</span> {fileAddress || <span className="text-gray-400 italic">Non disponible</span>}
                    </p>
                  </div>
                  <div className="rounded border border-yellow-100 bg-yellow-50 p-3">
                    <p className="mb-1 text-xs font-medium text-yellow-600">RNIC candidate (pas dans fichier)</p>
                    <p className="font-semibold text-gray-800">{entry.rnicEntry.name}</p>
                    <p className="font-mono text-sm text-gray-500">{entry.rnicEntry.immatriculation || '-'}</p>
                    <p className="mt-2 text-xs text-gray-600">
                      <span className="font-semibold">Adresse :</span> {rnicAddress || <span className="text-gray-400 italic">Non disponible</span>}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      mode={entry.confidenceScore && entry.confidenceScore >= 70 ? 'success' : entry.confidenceScore && entry.confidenceScore >= 50 ? 'warning' : 'info'}
                      text={`Confiance adresse : ${entry.confidenceScore || 0}%`}
                    />
                    {entry.mode === 'existing' && entry.status === 'validated' && <Badge mode="success" text="Validé" />}
                    {entry.mode === 'existing' && entry.status === 'pending' && <Badge mode="info" text="En attente" />}
                  </div>

                  <div className="flex gap-2">
                    {entry.mode === 'suggestion' && (
                      <>
                        <button
                          onClick={() => onAccept(entry)}
                          className="flex items-center gap-1 rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          Valider
                        </button>
                        <button
                          onClick={() => onReject(entry.pairKey)}
                          className="flex items-center gap-1 rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
                        >
                          <XCircleIcon className="h-4 w-4" />
                          Rejeter
                        </button>
                      </>
                    )}

                    {entry.mode === 'existing' && entry.status === 'pending' && (
                      <>
                        <button
                          onClick={() => onChangeStatus(fileKey, rnicKey, 'validated')}
                          className="flex items-center gap-1 rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          Valider
                        </button>
                        <button
                          onClick={() => onChangeStatus(fileKey, rnicKey, 'rejected')}
                          className="flex items-center gap-1 rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
                        >
                          <XCircleIcon className="h-4 w-4" />
                          Rejeter
                        </button>
                      </>
                    )}

                    {entry.mode === 'existing' && entry.status === 'validated' && (
                      <button
                        onClick={() => onChangeStatus(fileKey, rnicKey, 'pending')}
                        className="flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AddressMatchStep;
